import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Stack
} from '@mui/material';
import { useApiClient } from '../hooks/useApiClient';

interface TenantShare {
  email: string;
  permissionProfile: 'reader' | 'poweruser' | 'admin' | 'owner';
  createdAt: string;
  updatedAt: string;
}

interface TenantShareEditProps {
  tenantId: string;
  share?: TenantShare;
  onSave: (share: TenantShare) => void;
  onCancel: () => void;
}

const TenantShareEdit: React.FC<TenantShareEditProps> = ({ tenantId, share, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    email: '',
    permissionProfile: 'reader' as 'reader' | 'poweruser' | 'admin' | 'owner'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useApiClient();

  const isEdit = !!share;
  const isFormValid = formData.email.trim().length > 0 && 
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());

  useEffect(() => {
    if (share) {
      setFormData({
        email: share.email,
        permissionProfile: share.permissionProfile
      });
    }
  }, [share]);

  const handleSave = async () => {
    if (!isFormValid) return;

    setSaving(true);
    setError(null);

    try {
      const endpoint = isEdit 
        ? `/tenants/${tenantId}/shares/${encodeURIComponent(formData.email.trim())}`
        : `/tenants/${tenantId}/shares`;
      
      const data = isEdit ? {
        permissionProfile: formData.permissionProfile
      } : {
        email: formData.email.trim(),
        permissionProfile: formData.permissionProfile
      };

      const response = isEdit 
        ? await apiClient.put(endpoint, data)
        : await apiClient.post(endpoint, data);

      if (response.ok) {
        const savedShare = {
          email: formData.email.trim(),
          permissionProfile: formData.permissionProfile,
          createdAt: share?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        onSave(savedShare);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${isEdit ? 'update' : 'create'} share`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} share`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        <TextField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          fullWidth
          variant="outlined"
          disabled={isEdit || saving}
          error={!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())}
          helperText={!formData.email.trim() ? 'Email is required' : (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()) ? 'Invalid email format' : '')}
        />

        <FormControl fullWidth>
          <InputLabel>Permission</InputLabel>
          <Select
            value={formData.permissionProfile}
            label="Permission"
            onChange={(e) => setFormData({ ...formData, permissionProfile: e.target.value as 'reader' | 'poweruser' | 'admin' | 'owner' })}
            disabled={saving || (isEdit && formData.permissionProfile === 'owner')}
          >
            <MenuItem value="reader">Reader</MenuItem>
            <MenuItem value="poweruser">Power User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="owner">Owner</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isFormValid || saving}
          >
            {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default TenantShareEdit;